
import {ref, computed, watch} from 'vue';

export function useBaseTable(props, initTableData) {
	
	interface StyleClass {
		backgroundColor?: string;
	}
	
	const srcId = ref('');
	const srcType = ref(0);
	const srcSubType = ref(0);
	
	const sortColumn = ref(''); 
	const sortOrderAsc = ref(true); 
	
	const setting = ref(props.tableSetting);
	const pageSize = ref(props.tableSetting.pageSize);
	const pageStartIndex = ref(props.tableSetting.pageStartIndex);
	const errorMessage = ref(props.tableSetting.errorMessage);
	const rows = ref(props.tableRows);
	const columns = ref(props.tableColumns);
	const totalCount = ref(props.tableTotalCount);
	const header = ref({});
	
	const preparedRows = computed(() => {
		let newRows = rows.value;
		if (sortColumn.value && pageSize.value>0) { //sorting if sort column is choosen
			newRows = newRows.slice().sort(function (first, second) {
				if (first.folder!=second.folder) {//check if folder mode
					//return (first.folder ? 1 : -1) * sortOrder.value;
					return first.folder ? -1 : 1;
				}
				
				let firstValue;
				if (Boolean(first[sortColumn.value].rowValue)) {
					firstValue = first[sortColumn.value].rowValue;
				}
				else {
					firstValue = first[sortColumn.value].value;
				}

				let secondValue;
				if (Boolean(second[sortColumn.value].rowValue)) {
					secondValue = second[sortColumn.value].rowValue;
				}
				else {
					secondValue = second[sortColumn.value].value;
				}

				if (firstValue === secondValue) {
					return 0;
				}
				else {
					return (firstValue > secondValue ? 1 : -1) * sortOrder.value;
				}
			})
		}
		//console.log("psi:"+pageStartIndex.value+" + pse:"+pageEndIndex.value);
		return newRows.slice(pageStartIndex.value, pageEndIndex.value);
	});
	
	const sortOrder = computed(() => {
		return sortOrderAsc.value?1:-1;
	});
	
	const pageEndIndex = computed(() => {
		let indexEnd: number = pageStartIndex.value + pageSize.value;
		return indexEnd<totalCount.value?indexEnd:totalCount.value;
	});
	
	/*const maxPage = computed(() => {
		return totalCount.value>pageSize.value?Math.ceil(totalCount.value*1.0/pageSize.value)-1:0;
	});*/
	
	const isEmpty = computed(() => {
		return  rows.value === null || rows.value === undefined || rows.value.length===0;
	});
	
	const isError = computed(() => {
		return  errorMessage.value !== null && errorMessage.value !== undefined && errorMessage.value.length>0;
	});
	
	const numberOfColumns = computed(() => {
		return headerColumns.value?headerColumns.value.length:0
	});
	
	const headerColumns = computed(() => {
		return columns.value?columns.value.filter(column => !column.rowType):[];
	});
	
	const rowColumns = computed(() => {
		return columns?columns.value.filter(column => column.rowType):[];
	});
	
	const numberOfDataColumns = computed(() => {
		let numCols: number = numberOfColumns.value;
		if (hasEditColumn.value) {
			numCols--;
		}
		if (hasDeleteColumn.value) {
			numCols--;
		}

		return numCols>0?numCols:0;
	});
	
	const hasEditColumn = computed(() => {
		let column = columns.value.find(column => column.id==='EDIT');
		return column !== null && column !== undefined;
	});
	
	const hasDeleteColumn = computed(() => {
		let column = columns.value.find(column => column.id==='DELETE');
		return column !== null && column !== undefined;
	});
	
	function sortBy(key: string): void {
		if (sortColumn.value===key) {
			sortOrderAsc.value=!sortOrderAsc.value;
		}
		else {
			sortColumn.value = key;
			sortOrderAsc.value=true;
		}
	}
	
	function rowClass(row, index: number): string[] {
		let rowClasses: string[] = [];
		if (setting.value.rowClass) rowClasses.push(setting.value.rowClass);
		else if (index%2===0) rowClasses.push(setting.value.evenRowClass);
		else if (index%2===1) rowClasses.push(setting.value.oddRowClass);

		if (row.rowClass !== null && row.rowClass !== undefined) rowClasses.push(row.rowClass);

		return rowClasses;
	}
	
	function rowStyle(row): string {
		if (row.rowStyle !== null && row.rowStyle !== undefined) return row.rowStyle;
		else return "";
	}
	
	function sortStyle(column, rowIndex: number) {
		let sortStyle: StyleClass = {};
		if (sortColumn.value===column.id) {
			let colorIndex = findColorIndex(rowIndex);
			//console.log("rowIndex:"+rowIndex+" / colorIndex:"+colorIndex);
			sortStyle.backgroundColor=setting.value.sortColors[colorIndex];
		}
		return sortStyle;
	}
	
	function findColorIndex(rowIndex: number): number {
		let colorIndex: number = sortOrderAsc.value?rowIndex:rowIndex+1; //pageSize==50
		if (pageSize.value===10) {
			colorIndex = (sortOrderAsc.value?rowIndex:rowIndex+1)*5;
		}
		else if (pageSize.value===20) {
			colorIndex = Math.round((sortOrderAsc.value?rowIndex:rowIndex+1)*2.5);
		}
		return sortOrderAsc.value?colorIndex:setting.value.sortColors.length-colorIndex;
	}
	
	function firstPage(): void {
		pageStartIndex.value=0;
	}
	
	function lastPage(): void {
		pageStartIndex.value=totalCount.value-pageSize.value;
	}
	
	function nextPage(): void {
		pageStartIndex.value += pageSize.value;
		if (pageStartIndex.value>totalCount.value) lastPage();

	}
	
	function prevPage(): void {
		pageStartIndex.value -= pageSize.value;
		if (pageStartIndex.value<0) firstPage();
	}
	
	function initPage(totalCount: number): void {
		if (!Boolean(props.disableClientStorage)) {
			try {
				let data = JSON.parse(sessionStorage.getItem(storageSessionKey()));
				if (data!==null && data !== undefined) {
					pageStartIndex.value=data.pageStartIndex;
					sortColumn.value=data.sortColumn;
					sortOrderAsc.value=data.sortOrderAsc;
					return;
				}
			} catch(e) {
				console.log("Can't found previous saved config at sessionStorage, use server-side");
			}
		}

		let startIndex: number = setting.value.page*pageSize.value;
		if (startIndex>totalCount) startIndex=totalCount;
		pageStartIndex.value=startIndex;
	}
	
	function initPageSize(pageSizeFromServer: number): void {
		let newPageSize: number = pageSizeFromServer;
		//localStorage+sessionStorage
		if (!Boolean(props.disableClientStorage)) {
			let pageSizeFromLS: number = parseInt(localStorage.getItem(storageKey("pageSize")));
			if (pageSizeFromLS>0) newPageSize=pageSizeFromLS;
		}
		pageSize.value=newPageSize;
	}
	
	
	function loadTable(restApi?: string): Promise<boolean> {
		 return fetch(restApi?restApi:props.restApi)
			.then(response => response.json())
			.then(data => {
				//console.log("Loaded columns:"+JSON.stringify(data.tableColumns));
				//console.log("Loaded rows:"+JSON.stringify(data.tableRows));
				//console.log("Loaded sums:"+JSON.stringify(data.tableSums));
				//console.log("Loaded settings:"+JSON.stringify(data.setting));
				//update data
				srcId.value=data.srcId;
				srcType.value=data.srcType;
				srcSubType.value=data.srcSubType;
				totalCount.value=data.tableTotalCount;
				setting.value=data.setting;
				initPageSize(data.setting.pageSize);
				initPage(data.tableTotalCount);
				errorMessage.value=data.setting.errorMessage;

				columns.value=data.tableColumns;
				rows.value=data.tableRows;
				initTableData(data);
				return true;
			})
			.catch((error) => {
				console.log("error loading table:"+error);
				errorMessage.value = error;
				return false;
			});
	}
	
	function refresh(restApi?: string): Promise<boolean>  {
		return fetch(restApi?restApi:refreshRestApi())
			.then(response => response.json())
			.then(data => {
				//console.log("Loaded rows:"+JSON.stringify(data.tableRows));
				//console.log("Loaded sums:"+JSON.stringify(data.tableSums));
				rows.value=data.tableRows;
				initTableData(data);
				totalCount.value=data.tableTotalCount;
				return true;
			})
			.catch((error) => {
				console.log("error refresh data:"+error);
				errorMessage.value = error;
				return true;
			});
	}
	
	function refreshRestApi(): string {
		let url: string = props.restApi;
		if (url.indexOf("?")<0) url +="?";
		for (const [key, value] of Object.entries(header.value)) {
			if (Boolean(value)) {
				url += "&"+key+"="+value;
			}
		}
		//console.log("refresh url:"+url);
		return url;
	}
	
	function storageKey(property: string): string {
		let key: string = "CrmSubTable."+setting.value.sourceObject+"."+property;
		//console.log("key:"+key);
		return key;
	}
	
	function storageSessionKey(): string {
		return storageKey(setting.value.sourceId);
	}
	
	function saveToSession(sortColumn: string, sortOrderAsc: boolean, pageStartIndex: number): void {
		let data: string = JSON.stringify({
						  sortColumn: sortColumn,
						  sortOrderAsc: sortOrderAsc,
						  pageStartIndex: pageStartIndex
						})
		sessionStorage.setItem(storageSessionKey(),data);
		//console.log("Stored to sessionStorage:"+data);
	}
	
	watch(pageSize, (newPageSize: number) => {
		if (!Boolean(props.disableClientStorage)) {
			localStorage.setItem(storageKey("pageSize"),newPageSize.toString());
		}
	});
	
	watch(sortColumn, (newSortColumn: string) => {
		if (!Boolean(props.disableClientStorage)) {
			saveToSession(newSortColumn,sortOrderAsc.value,pageStartIndex.value);
		}
	});
	
	watch(sortOrderAsc, (newSortOrderAsc: boolean) => {
		if (!Boolean(props.disableClientStorage)) {
			saveToSession(sortColumn.value,newSortOrderAsc,pageStartIndex.value);
		}
	});
	
	watch(pageStartIndex, (newPageStartIndex: number) => {
		if (!Boolean(props.disableClientStorage)) {
			saveToSession(sortColumn.value,sortOrderAsc.value,newPageStartIndex);
		}
	});
	
	return  {
				setting,sortColumn,sortOrderAsc,pageSize,pageStartIndex,
				rows,columns,totalCount,header,errorMessage,
				preparedRows,pageEndIndex,isEmpty,isError,numberOfColumns,headerColumns,
				rowColumns,numberOfDataColumns,hasEditColumn,hasDeleteColumn,
				sortBy,rowClass,rowStyle,sortStyle,
				firstPage,lastPage,nextPage,prevPage,loadTable,refresh,refreshRestApi
			};
}

